import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { useMemo, useState } from "react";
import { Calendar, ArrowRight } from "lucide-react";
import { PageHero } from "@/components/PageHero";
import { img } from "@/lib/images";
import { articles } from "@/lib/articles";
import { SmartImage } from "@/components/SmartImage";
import { canonicalLinks, routeMeta } from "@/lib/seo";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      ...routeMeta("/blog"),
      { title: "Blog — Tax & Accounting Insights | CCOB And Company PA" },
      { name: "description", content: "Read the latest financial insights, tax updates, IRS news, and accounting tips from CCOB And Company PA." },
      { property: "og:title", content: "Blog — CCOB And Company PA" },
      { property: "og:description", content: "Financial insights, tax updates, and IRS news for individuals and businesses." },
    ],
    links: canonicalLinks("/blog"),
  }),
  component: BlogPage,
});

function BlogPage() {
  const fadeUp = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true }, transition: { duration: 0.6 } };
  const categories = useMemo(() => ["All", ...Array.from(new Set(articles.map((a) => a.category)))], []);
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? articles : articles.filter((a) => a.category === active);

  return (
    <>
      <PageHero eyebrow="Blog" title="Financial Insights from a Trusted CPA" subtitle="Tax updates, accounting tips, IRS news, and educational content — published regularly." image={img.heroBlog} />
      <section className="py-24">
        <div className="container-x">
          <div className="flex flex-wrap gap-2 mb-12 justify-center">
            {categories.map((c) => {
              const isActive = c === active;
              return (
                <button
                  key={c}
                  onClick={() => setActive(c)}
                  className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition ${
                    isActive
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-transparent text-muted-foreground border-border hover:border-gold hover:text-primary"
                  }`}
                >
                  {c}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-7">
            {filtered.map((p) => (
              <motion.article
                key={p.slug}
                {...fadeUp}
                className="group bg-card border border-border rounded-2xl overflow-hidden hover:border-gold transition flex-grow basis-full md:basis-[calc(50%-0.875rem)] lg:basis-[calc(33.333%-1.167rem)] flex flex-col"
              >
                <Link to="/blog/$slug" params={{ slug: p.slug }} className="flex flex-col h-full">
                  <div className="overflow-hidden h-64 flex-shrink-0">
                    <SmartImage src={p.image} alt={p.title} width={1024} height={1024} sizes="(max-width: 767px) 100vw, (max-width: 1023px) 50vw, 33vw" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-widest text-gold">{p.category}</div>
                    <h2 className="font-display text-xl text-primary mt-2 group-hover:text-gold transition">{p.title}</h2>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{p.excerpt}</p>
                    <div className="flex items-center justify-between mt-5 pt-5 border-t border-border">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground"><Calendar className="h-3.5 w-3.5" /> {p.date}</div>
                      <span className="inline-flex items-center gap-1 text-sm text-primary group-hover:text-gold transition">Read <ArrowRight className="h-4 w-4" /></span>
                    </div>
                  </div>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
